import { z } from "zod";

import { isAxiosError } from "./isAxiosError";
import { DEFAULT_ERROR_NAME, type RemiErrorDeprecatedExtensionOptions, type RemiErrorDeprecatedInput } from "./types";

const TrpcErrorShape = z.object({
  name: z.literal("TRPCError"),
  code: z.string(),
});

export class RemiErrorDeprecated extends Error {
  readonly pkg: string | undefined;
  readonly path: string | undefined;
  readonly originalError: Error | undefined;
  readonly attributes: RemiErrorDeprecatedExtensionOptions | undefined;

  constructor({ message, originalError, options = {} }: RemiErrorDeprecatedInput) {
    const { pkg, path, attributes } = options;
    super(message);

    this.name = originalError?.name ? originalError.name : DEFAULT_ERROR_NAME;

    this.path = path ?? undefined;
    this.pkg = pkg ?? undefined;
    this.attributes = attributes ?? undefined;

    this.originalError = originalError ?? undefined;

    Object.defineProperties(this, {
      message: {
        writable: true,
        enumerable: true,
      },
      name: { enumerable: false },
      pkg: { enumerable: false },
      path: { enumerable: false },
      originalError: { enumerable: false },
    });

    if (originalError?.stack !== undefined) {
      Object.defineProperty(this, "stack", {
        value: originalError.stack,
        writable: true,
        configurable: true,
      });
    } else {
      Object.defineProperty(this, "stack", {
        // oxlint-disable-next-line remi/no-vanilla-error -- captures a stack trace; this Error is never thrown
        value: new Error("stack").stack,
        writable: true,
        configurable: true,
      });
    }
  }

  get [Symbol.toStringTag](): string {
    return this.name;
  }

  override toString(): string {
    let output = this.message;

    if (this.pkg) {
      output += `\n\nPackage: ${this.pkg}`;
    }
    if (this.path) {
      output += `\n\nPath: ${this.path}`;
    }
    if (this.originalError) {
      const trpc = TrpcErrorShape.safeParse(this.originalError as unknown);
      if (isAxiosError(this.originalError)) {
        output += "\n\nType: AxiosError";
        output += `\n\nCode: ${this.originalError.code}`;
        output += `\n\nRequest: ${this.originalError.request}`;
        output += `\n\nResponse: ${this.originalError.response?.data}`;
      } else if (trpc.success) {
        output += "\n\nType: TRPCError";
        output += `\n\nCode: ${trpc.data.code}`;
        output += `\n\nMessage: ${this.originalError.message}`;
      }
    }
    if (this.attributes) {
      Object.entries(this.attributes).forEach(([k, v]) => {
        output += `\n\n[attributes] ${k}: ${v}`;
      });
    }
    return output;
  }
}
